import React, { useState, useEffect, useRef, useMemo } from 'react';
import { ExternalLink, Music2 } from 'lucide-react';

// Extracts { type, id } from a Spotify open URL (track|album|playlist|episode|show|artist)
const parseSpotifyUrl = (url) => {
  if (!url || typeof url !== 'string') return null;
  const match = url.match(
    /open\.spotify\.com\/(intl-[a-z]+\/)?(track|album|playlist|episode|show|artist)\/([a-zA-Z0-9]+)/
  );
  if (!match) return null;
  return { type: match[2], id: match[3] };
};

const EmbedFallback = ({ url, title, artwork, fallbackLabel }) => (
  <div
    className="rounded-xl border border-silver-400/30 bg-gradient-to-br from-silver-900/40 to-black p-5 flex items-center gap-4"
    data-testid="spotify-embed-fallback"
  >
    {artwork ? (
      <img src={artwork} alt={title} className="w-16 h-16 rounded-md object-cover flex-shrink-0" />
    ) : (
      <div className="w-16 h-16 rounded-md bg-silver-400/20 flex items-center justify-center flex-shrink-0">
        <Music2 size={28} className="text-silver-400" />
      </div>
    )}
    <div className="flex-1 min-w-0">
      <p className="text-white font-semibold truncate">{title}</p>
      <p className="text-white/50 text-xs mt-1">
        Preview unavailable — open the full track on Spotify.
      </p>
    </div>
    {url && (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-2 px-4 py-2 rounded-lg bg-silver-400 hover:bg-silver-300 text-black text-xs font-bold uppercase tracking-wider transition-colors flex-shrink-0"
        data-testid="spotify-embed-fallback-link"
      >
        <ExternalLink size={14} />
        {fallbackLabel}
      </a>
    )}
  </div>
);

const EmbedFrame = ({ src, title, height, status, onLoad, onError }) => (
  <div className="relative rounded-xl overflow-hidden" data-testid="spotify-embed">
    {status === 'loading' && (
      <div
        className="absolute inset-0 flex items-center justify-center bg-silver-900/30 animate-pulse"
        style={{ minHeight: height }}
        data-testid="spotify-embed-skeleton"
      >
        <Music2 size={28} className="text-silver-400/60" />
      </div>
    )}
    <iframe
      title={title}
      src={src}
      width="100%"
      height={height}
      frameBorder="0"
      allow="clipboard-write; encrypted-media; fullscreen; picture-in-picture"
      loading="lazy"
      onLoad={onLoad}
      onError={onError}
      style={{ borderRadius: '12px', display: 'block' }}
    />
  </div>
);

const SpotifyEmbed = ({
  url,
  title = 'Spotify',
  artwork,
  height = 152,
  fallbackLabel = 'Open in Spotify',
}) => {
  const parsed = useMemo(() => parseSpotifyUrl(url), [url]);
  const [status, setStatus] = useState(parsed ? 'loading' : 'error');
  const timeoutRef = useRef(null);

  useEffect(() => {
    if (!parsed) return undefined;
    // If the iframe never fires onLoad within 6s assume it's blocked/broken
    timeoutRef.current = setTimeout(() => {
      setStatus((prev) => (prev === 'loading' ? 'error' : prev));
    }, 6000);
    return () => clearTimeout(timeoutRef.current);
  }, [parsed]);

  const handleLoad = () => {
    clearTimeout(timeoutRef.current);
    setStatus('ready');
  };

  const handleError = () => {
    clearTimeout(timeoutRef.current);
    setStatus('error');
  };

  if (status === 'error') {
    return <EmbedFallback url={url} title={title} artwork={artwork} fallbackLabel={fallbackLabel} />;
  }

  return (
    <EmbedFrame
      src={`https://open.spotify.com/embed/${parsed.type}/${parsed.id}?utm_source=generator&theme=0`}
      title={title}
      height={height}
      status={status}
      onLoad={handleLoad}
      onError={handleError}
    />
  );
};

export default SpotifyEmbed;
